import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { FaCheckCircle, FaDesktop } from 'react-icons/fa';

/**
 * Shown after Stripe Checkout when purchase was started from ZapRF desktop app.
 * In credit mode the server grants credits on the verified session; the desktop
 * app refetches the authoritative balance -- nothing is minted in the browser.
 */
const PaymentCompleteDesktop = () => {
  const [searchParams] = useSearchParams();
  const { isDarkMode } = useTheme();
  const { token } = useAuth();
  const sessionId = searchParams.get('session_id');

  // Refetch the authoritative balance rather than incrementing anything locally.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      for (let attempt = 0; attempt < 3 && !cancelled; attempt += 1) {
        try {
          await fetch(
            `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/credits/account`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
        } catch (err) {
          console.error('Error refetching credit balance:', err);
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-purple to-brand-red p-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8">
        <FaCheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h2 className={`text-2xl font-bold text-center mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Payment received. Your credits are being added.
        </h2>
        <p className={`text-center mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Your payment was successful. Return to the ZapRF desktop application to
          see your updated credit balance.
        </p>
        <div className={`flex items-center justify-center gap-2 mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <FaDesktop className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Switch back to ZapRF on your computer
          </span>
        </div>
        {sessionId && (
          <p className={`text-center text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Reference: {sessionId.slice(0, 20)}...
          </p>
        )}
        <p className={`text-center text-sm mt-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          You can close this browser tab.
        </p>
      </div>
    </div>
  );
};

export default PaymentCompleteDesktop;
